
import { Component, Vue } from "vue-property-decorator";
import Moment from "moment";
import { OfferRequestDto } from "@/dtos/offerRequestDto";
import { OfferRequestState } from "@/dtos/offerRequestState";
import { OfferRequestOrderType } from "@/dtos/offerRequestOrderType";
import { OfferRequestPositionDto } from "@/dtos/offerRequestPositionDto";

@Component
export default class OfferRequests extends Vue {
	private requests: OfferRequestDto[] = new Array<OfferRequestDto>();
	private isLoading: boolean = false;

	// Keys of the locale entries below offerRequest.useCases and offerRequest.steps.
	// Kept here so the template does not repeat the same markup three times.
	private useCases = [
		{ key: "bulk", icon: "mdi-package-variant-closed" },
		{ key: "series", icon: "mdi-repeat-variant" },
		{ key: "automation", icon: "mdi-robot-industrial" },
		{ key: "individual", icon: "mdi-account-tie-voice" },
	];
	private steps = ["step1", "step2", "step3"];

	private mounted() {
		this.loadRequests();
	}

	private get hasRequests(): boolean {
		return this.requests.length > 0;
	}

	// Number of article positions in the current cart. The cart is null until the
	// store has initialised it, see setupShoppingCart in App.vue.
	private get cartItemCount(): number {
		const cart = this.$store.state.currentCart;
		if (cart === null || cart === undefined) {
			return 0;
		}
		return cart.cartItems.length;
	}

	private get hasCartItems(): boolean {
		return this.cartItemCount > 0;
	}

	/**
	 * Loads the offer requests of the current customer. The backend endpoint does not
	 * exist yet, so for now every customer sees the empty state with the call to action.
	 */
	private loadRequests() {
		this.requests = new Array<OfferRequestDto>();
		this.isLoading = false;
	}

	private createRequest() {
		this.$router.push({ name: "CreateOfferRequest" });
	}

	/**
	 * Opens a single request. The detail view does not exist yet, so this is still a
	 * no-op. No customer can reach it today because the list is always empty.
	 */
	private openRequest(request: OfferRequestDto) {
		console.log(`Detail view for offer request ${request.id} is not implemented yet.`);
	}

	private formatDate(value: Date): string {
		return Moment(String(value)).format("DD.MM.YYYY");
	}

	// Tile figures are derived from the positions, so they cannot drift apart from them.
	private positionCount(request: OfferRequestDto): number {
		return request.positions.length;
	}

	private totalQuantity(request: OfferRequestDto): number {
		return request.positions.reduce(
			(sum: number, position: OfferRequestPositionDto) => sum + position.quantity,
			0
		);
	}

	private isRecurring(request: OfferRequestDto): boolean {
		return request.orderType === OfferRequestOrderType.Recurring;
	}

	private stateLabel(state: OfferRequestState): string {
		switch (state) {
			case OfferRequestState.Submitted:
				return this.$t("offerRequest.states.submitted").toString();
			case OfferRequestState.InReview:
				return this.$t("offerRequest.states.inReview").toString();
			case OfferRequestState.OfferAvailable:
				return this.$t("offerRequest.states.offerAvailable").toString();
			case OfferRequestState.Accepted:
				return this.$t("offerRequest.states.accepted").toString();
			case OfferRequestState.Declined:
				return this.$t("offerRequest.states.declined").toString();
			case OfferRequestState.Expired:
				return this.$t("offerRequest.states.expired").toString();
			default:
				return this.$t("offerRequest.states.unknown").toString();
		}
	}

	// Colour of the left rail and of the state chip. Yellow marks a step that is
	// currently with Blexon, green a result the customer can act on, grey a closed request.
	private stateColor(state: OfferRequestState): string {
		switch (state) {
			case OfferRequestState.Submitted:
				return "#9aa1a9";
			case OfferRequestState.InReview:
				return "#efbb20";
			case OfferRequestState.OfferAvailable:
				return "#2f855a";
			case OfferRequestState.Accepted:
				return "#1f5c3f";
			default:
				return "#c8ccd1";
		}
	}

	private stateChipStyle(state: OfferRequestState) {
		const color = this.stateColor(state);
		return {
			color: color,
			borderColor: color,
		};
	}
}
